import type { ToolDefinition, ToolType } from "../types";

const GIF_MAKER_TOOL_TYPE = {
  label: "GIF Tool",
  icon: "Film",
} as const satisfies ToolType;

const gifMakerTool: ToolDefinition = {
  slug: "gif-maker",
  name: "Free GIF Maker for WhatsApp, Telegram & Discord | Video & Image to GIF",
  title: "GIF Maker for Chats & Social Media",
  description:
    "Turn videos and images into small, looping GIFs made for chat apps. Convert MP4, MOV, WebM, and images into lightweight animations for WhatsApp, Telegram, and Discord. Free, no watermark, no account.",
  longDescription:
    "Make a GIF from a video clip or an image and get a small, looping file that sends cleanly on chat apps. Upload an MP4, MOV, or WebM and trim up to five seconds, or convert a PNG, JPG, or animated GIF, then tune the frame rate, width, quality, and playback direction. Output is compressed and watermark-free, with no account required.",
  toolType: {
    label: "GIF Maker",
    icon: "Film",
  },
  heroBadges: [
    "100% Free",
    "No Watermark",
    "No Account Required",
    "Video & Image to GIF",
    "Chat-App Optimized",
    "Works on Mobile",
  ],
  category: "Conversion Tools",
  categorySlug: "conversion-tools",
  href: "/tools/gif-maker",
  api: "/api/v1/image-to-gif",
  keywords: [
    "snappy animated gif maker",
    "convert mp4 to looping image",
    "frame rate quality inspector",
    "how to create discord emotes",
    "turn mov into meme graphic",
    "online boomerang generator tool",
    "whatsapp gif size checker",
    "best settings for twitter videos",
    "change webm to visual loop",
    "live photo transformation software",
  ],
  pageContent: {
    sections: {
      steps: {
        title: "How to Make a GIF for WhatsApp, Telegram, and More",
        description:
          "Convert a video clip or an image into a small, looping GIF, then download or share it straight to your favorite chat app.",
      },
      benefits: {
        title: "Why Use This GIF Maker for Chat Apps?",
        description:
          "A free GIF maker that keeps files small enough to send on messaging apps, with real controls for trimming, frame rate, width, and playback.",
      },
      faqs: {
        title: "Frequently Asked Questions",
        description:
          "Practical answers about making GIFs for WhatsApp, Telegram, and Discord, converting video to GIF, and fixing common sharing problems.",
      },
    },
    introBlocks: [
      {
        kind: "text",
        title:
          "A Free Online GIF Maker Built for WhatsApp, Telegram, and Discord",
        level: "h2",
        paragraphs: [
          "Most GIF makers are built for the open web, so they hand you a giant file that chat apps reject or turn into a blurry still. This tool works the other way around: it converts your video or image into a small, smooth, looping GIF that is compressed automatically, so it sends cleanly on WhatsApp, Telegram, and Discord without the file too large error.",
          "You can turn a video into a GIF by uploading an MP4, MOV, or WebM clip, trimming the exact moment on the timeline, and tuning the frame rate, width, quality, and playback direction. You can also turn an image into a GIF: drop in a PNG or JPG, or load an animated GIF to re-time it. Either way you get a clean, downloadable GIF, with nothing to install.",
        ],
        note: "For chat apps, aim small: a width of 480px or less, around 10 to 15 FPS, and a clip under 5 seconds keeps the file light and easy to send.",
      },
      {
        kind: "callouts",
        groups: [
          {
            title: "What You Can Convert to a GIF",
            layout: "list",
            iconColor: "primary",
            items: [
              {
                label: "Video Clips (MP4, MOV, WebM)",
                icon: "Video",
                description:
                  "Trim up to 5 seconds, set the frame rate, width, and quality, and reverse playback for a boomerang loop.",
                accent: "primary",
              },
              {
                label: "Images (PNG, JPG)",
                icon: "Image",
                description:
                  "Turn a single picture into a looping GIF for quick reactions, simple graphics, and meme captions.",
                accent: "primary",
              },
              {
                label: "Existing Animated GIFs",
                icon: "Film",
                description:
                  "Re-time or shrink a GIF you already have. Its frames are kept while you change how fast each one plays.",
                accent: "primary",
              },
            ],
          },
        ],
      },
      {
        kind: "text",
        title: "The Right GIF Settings for Messaging Apps",
        level: "h2",
        paragraphs: [
          "Messaging apps treat GIFs differently from websites, which is why settings matter. WhatsApp plays short GIFs inline but can show a very large one as a frozen image. Telegram re-encodes GIFs into a looping MP4 and autoplays them, with files under roughly 10MB loading instantly. Discord autoplays GIFs but caps free uploads at 10MB per message.",
          "This tool leans into those limits for you. When you convert MP4 to GIF or change WebM to a visual loop, the output is compressed toward a small target size. Lower frame rates and narrower widths shrink the file fast, so a 10 to 15 FPS GIF at 480px almost always sends, even on a slow connection.",
        ],
      },
      {
        kind: "table",
        tables: [
          {
            title: "Best GIF Size Targets by Chat and Social App",
            description:
              "Use these targets so your GIF animates, autoplays, and sends without errors. Smaller is faster and less likely to be rejected.",
            columns: [
              "App / Platform",
              "Practical Size Target",
              "How GIFs Behave",
            ],
            rows: [
              {
                "App / Platform": "WhatsApp",
                "Practical Size Target": "Under 16 MB (aim 1-2 MB)",
                "How GIFs Behave":
                  "Short GIFs animate inline; very large ones can show as a still, so keep them small and under 5s.",
              },
              {
                "App / Platform": "Telegram",
                "Practical Size Target": "Under 10 MB",
                "How GIFs Behave":
                  "Converts GIFs to a looping MP4 and autoplays them; small files play instantly.",
              },
              {
                "App / Platform": "Discord (free)",
                "Practical Size Target": "Under 10 MB",
                "How GIFs Behave":
                  "Autoplays GIFs; the free cap is 10 MB per message, higher with Nitro.",
              },
              {
                "App / Platform": "Messenger / Instagram DM",
                "Practical Size Target": "Around 8-15 MB",
                "How GIFs Behave":
                  "Plays GIFs inline; lighter files send faster on mobile data.",
              },
              {
                "App / Platform": "X (Twitter)",
                "Practical Size Target": "5 MB web, 15 MB mobile",
                "How GIFs Behave":
                  "Autoplays in the feed; clips under 5 seconds loop best.",
              },
              {
                "App / Platform": "Reddit / Web pages",
                "Practical Size Target": "Under 5 MB",
                "How GIFs Behave":
                  "Smaller GIFs load faster and protect page speed.",
              },
            ],
            note: "Limits change over time; aim for the smallest file that still looks good.",
            emphasisColumns: ["App / Platform"],
            monospaceColumns: ["Practical Size Target"],
          },
        ],
      },
    ],
    steps: [
      {
        number: "01",
        title: "Upload your video or image",
        description:
          "Drag and drop an MP4, MOV, or WebM video, or a PNG, JPG, or GIF image, up to 20MB. The tool shows a live preview.",
      },
      {
        number: "02",
        title: "Trim and adjust your settings",
        description:
          "For video, trim up to 5 seconds and set the frame rate, width, quality, and optional reverse. For images, set the frame duration.",
      },
      {
        number: "03",
        title: "Generate the GIF",
        description:
          "Click Generate GIF. The tool processes your selection and compresses it toward a small, chat-friendly file.",
      },
      {
        number: "04",
        title: "Preview, download, and share",
        description:
          "Check the looping preview, then download with no watermark or use the share button to send it to a chat app.",
      },
    ],
    benefits: [
      {
        icon: "Gauge",
        title: "Small, Chat-Friendly Files",
        description:
          "Every GIF is compressed toward a light target size, so it sends on WhatsApp, Telegram, and Discord without hitting upload limits.",
      },
      {
        icon: "Scissors",
        title: "Built-In Video Trimmer",
        description:
          "Isolate the exact moment on the timeline. Clips stay short, up to 5 seconds, which is ideal for quick chat reactions.",
      },
      {
        icon: "Settings",
        title: "Full Quality Control",
        description:
          "Adjust frame rate, width, and quality for video, or frame duration for images, to balance smoothness against file size.",
      },
      {
        icon: "RefreshCcw",
        title: "Reverse and Boomerang Loops",
        description:
          "Flip playback to create a boomerang-style loop that runs backwards, making reactions and memes more eye-catching.",
      },
      {
        icon: "Droplet",
        title: "No Watermark, No Account",
        description:
          "Your GIF is clean and yours to share. The tool is free and never asks you to sign up or log in.",
      },
    ],
    faqs: [
      {
        question: "How do I make a GIF for WhatsApp that actually animates?",
        answer:
          "Keep the file small and the clip short. WhatsApp plays small GIFs inline but can show a large one as a frozen image. This tool compresses automatically, so trim to a few seconds, keep the width modest, and send it through the normal media flow.",
      },
      {
        question: "What is the best way to make a GIF for Telegram?",
        answer:
          "Telegram converts GIFs into a looping MP4 and autoplays them, and files under about 10MB load instantly. Convert your clip at 10 to 15 FPS and the output stays well under that, so it plays the moment it arrives.",
      },
      {
        question:
          "How do I fix the file too large error when posting a GIF to Discord?",
        answer:
          "Discord caps free uploads at 10MB per message. To stay under it, trim shorter, drop the width to 400 to 480px, and lower the frame rate to 10 FPS. The output is already compressed, so a short GIF usually fits easily.",
      },
      {
        question: "Can I convert an MP4, MOV, or WebM video to a GIF?",
        answer:
          "Yes. Upload a video up to 20MB, trim up to 5 seconds, adjust the frame rate, width, and quality, then generate. It is an easy way to convert MP4 to a looping image or change WebM into a shareable loop.",
      },
      {
        question: "How do I turn an image or photo into a GIF?",
        answer:
          "Upload a PNG, JPG, or GIF and click Generate GIF. A single picture becomes a looping GIF, and an animated GIF keeps its frames while you adjust the frame duration.",
      },
      {
        question: "Why is my GIF shorter than my original video?",
        answer:
          "The trimmer caps each clip at 5 seconds. Short loops stay small enough to send on chat apps and play smoothly, so use the timeline to pick your best moment.",
      },
      {
        question: "How do I make a reverse or boomerang GIF?",
        answer:
          "After uploading a video, turn on the reverse option before generating. The GIF plays backwards, a quick way to make a boomerang-style loop with no extra software.",
      },
      {
        question: "Does the tool add a watermark or require an account?",
        answer:
          "No to both. Your GIF has no watermark and needs no sign-in. The upload limit is 20MB, so trim a larger source video first.",
      },
    ],
    useCases: [
      {
        label: "Chat & Group Reactions",
        icon: "MessageCircle",
        description:
          "Turn a quick clip into a small looping reaction for WhatsApp, Telegram, or any group chat.",
        accent: "primary",
      },
      {
        label: "Discord Communities",
        icon: "Gamepad2",
        description:
          "Trim funny gaming or stream moments into lightweight GIFs that stay under the free upload limit.",
        accent: "primary",
      },
      {
        label: "Memes & Social Posts",
        icon: "Share2",
        description:
          "Convert video highlights into shareable looping memes for X, Reddit, and messaging apps.",
        accent: "primary",
      },
    ],
    audiences: [
      {
        label: "Content Creators",
        icon: "Youtube",
        description:
          "Turn your best clips into shareable GIFs for posts, replies, and community chats.",
        accent: "accent",
      },
      {
        label: "Community Moderators",
        icon: "Users",
        description:
          "Make small, fast-loading GIFs for servers and group chats without watermarks or sign-ups.",
        accent: "accent",
      },
    ],
  },
};

export default gifMakerTool;
export { gifMakerTool };
